import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle2, ArrowLeft } from 'lucide-react';
import { ForgotPasswordSchema, type ForgotPasswordInput } from '@cutlyft/zod-schemas';
import { useForgotPassword } from '@/hooks/auth/use-forgot-password';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';

export default function ForgotPasswordPage() {
  const forgotPasswordMutation = useForgotPassword();

  const form = useForm<ForgotPasswordInput>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = (data: ForgotPasswordInput) => {
    forgotPasswordMutation.mutate(data);
  };

  const isSuccess = forgotPasswordMutation.isSuccess;

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-bg-secondary p-6 relative overflow-hidden">
      {/* Decorative premium gradient background glow */}
      <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full bg-primary/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-accent/5 blur-[120px] pointer-events-none" />

      <div className="w-full max-w-md z-10 space-y-6">
        {/* Brand logo display */}
        <div className="text-center">
          <span className="text-3xl font-extrabold tracking-tight text-primary">Cutlyft</span>
        </div>

        <Card className="border border-border/50 shadow-xl bg-bg-elevated/80 backdrop-blur-md">
          {!isSuccess ? (
            <>
              <CardHeader>
                <CardTitle className="text-2xl text-fg-primary flex items-center gap-2">
                  <Mail className="h-6 w-6 text-primary" />
                  <span>Reset Password</span>
                </CardTitle>
                <CardDescription className="text-fg-secondary">
                  Enter your email address and we'll send you a link to reset your password.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    {form.formState.errors.root && (
                      <div className="p-3 text-sm rounded bg-error-bg text-error border border-error/20">
                        {form.formState.errors.root.message}
                      </div>
                    )}

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Address</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="john.doe@example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full mt-2 bg-primary hover:bg-primary/95 text-primary-foreground font-semibold h-11"
                      disabled={forgotPasswordMutation.isPending}
                    >
                      {forgotPasswordMutation.isPending ? 'Sending Link...' : 'Send Reset Link'}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success-bg text-success mb-3">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <CardTitle className="text-2xl text-fg-primary">Check Your Email</CardTitle>
                <CardDescription className="text-fg-secondary pt-1">
                  We have sent a password reset link to your email address. If the account exists, you will receive the message shortly.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center text-sm text-fg-muted">
                Please also check your spam or junk folder if you don't see the email in your inbox.
              </CardContent>
            </>
          )}

          <CardFooter className="flex justify-center border-t border-border/30 pt-6">
            <Link to="/login" className="flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Login</span>
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
